/**
 * FFT Utilities for EEG Spectral Analysis
 *
 * Real FFT implementation with Welch's method for power spectrum estimation
 */

/**
 * Complex number representation
 */
interface Complex {
  real: number;
  imag: number;
}

/**
 * FFT Result
 */
export interface FFTResult {
  /** Frequency bins (Hz) */
  frequencies: number[];
  /** Power spectrum (μV²/Hz) */
  power: number[];
  /** Phase spectrum (radians) */
  phase: number[];
}

/**
 * Power Spectrum (alias for FFTResult for backward compatibility)
 */
export type PowerSpectrum = FFTResult;

/**
 * Spectrogram data structure
 */
export interface SpectrogramData {
  /** Time points (seconds) */
  times: number[];
  /** Frequency bins (Hz) */
  frequencies: number[];
  /** Magnitude matrix [time][frequency] in dB */
  magnitudes: number[][];
}

/**
 * Welch's method result
 */
export interface WelchResult {
  /** Frequency bins (Hz) */
  frequencies: number[];
  /** Power spectral density (μV²/Hz) */
  psd: number[];
  /** Power alias for backward compatibility */
  power?: number[];
  /** Frequency bands */
  bands: {
    delta: { power: number; range: [number, number] };
    theta: { power: number; range: [number, number] };
    alpha: { power: number; range: [number, number] };
    beta: { power: number; range: [number, number] };
    gamma: { power: number; range: [number, number] };
  };
}

/**
 * Cooley-Tukey FFT Algorithm
 * Radix-2 DIT (Decimation In Time)
 */
function fft(input: Complex[]): Complex[] {
  const n = input.length;

  // Base case
  if (n <= 1) return input;

  // Ensure power of 2
  if ((n & (n - 1)) !== 0) {
    throw new Error("FFT size must be a power of 2");
  }

  // Divide
  const even: Complex[] = [];
  const odd: Complex[] = [];

  for (let i = 0; i < n; i++) {
    if (i % 2 === 0) {
      even.push(input[i]);
    } else {
      odd.push(input[i]);
    }
  }

  // Conquer
  const evenFFT = fft(even);
  const oddFFT = fft(odd);

  // Combine
  const result: Complex[] = new Array(n);
  const halfN = n / 2;

  for (let k = 0; k < halfN; k++) {
    const angle = (-2 * Math.PI * k) / n;
    const twiddle: Complex = {
      real: Math.cos(angle),
      imag: Math.sin(angle),
    };

    // Complex multiplication: twiddle * oddFFT[k]
    const temp: Complex = {
      real: twiddle.real * oddFFT[k].real - twiddle.imag * oddFFT[k].imag,
      imag: twiddle.real * oddFFT[k].imag + twiddle.imag * oddFFT[k].real,
    };

    // Combine even and odd parts
    result[k] = {
      real: evenFFT[k].real + temp.real,
      imag: evenFFT[k].imag + temp.imag,
    };

    result[k + halfN] = {
      real: evenFFT[k].real - temp.real,
      imag: evenFFT[k].imag - temp.imag,
    };
  }

  return result;
}

/**
 * Apply Hamming window to reduce spectral leakage
 */
function hammingWindow(n: number): number[] {
  const window: number[] = new Array(n);
  for (let i = 0; i < n; i++) {
    window[i] = 0.54 - 0.46 * Math.cos((2 * Math.PI * i) / (n - 1));
  }
  return window;
}

/**
 * Apply Hanning window
 */
function hanningWindow(n: number): number[] {
  const window: number[] = new Array(n);
  for (let i = 0; i < n; i++) {
    window[i] = 0.5 * (1 - Math.cos((2 * Math.PI * i) / (n - 1)));
  }
  return window;
}

/**
 * Find next power of 2 greater than or equal to n
 */
function nextPowerOf2(n: number): number {
  return Math.pow(2, Math.ceil(Math.log2(n)));
}

/**
 * Zero-pad array to power of 2 length
 */
function zeroPad(data: number[], targetLength: number): number[] {
  const padded = new Array(targetLength).fill(0);
  for (let i = 0; i < data.length; i++) {
    padded[i] = data[i];
  }
  return padded;
}

/**
 * Compute FFT on real-valued signal
 */
export function computeFFT(
  signal: number[],
  samplingRate: number,
  windowType: "hamming" | "hanning" | "none" = "hamming",
): FFTResult {
  // Ensure power of 2
  const fftSize = nextPowerOf2(signal.length);
  const paddedSignal = zeroPad(signal, fftSize);

  // Apply window function
  let windowedSignal = paddedSignal;
  if (windowType === "hamming") {
    const window = hammingWindow(fftSize);
    windowedSignal = paddedSignal.map((val, i) => val * window[i]);
  } else if (windowType === "hanning") {
    const window = hanningWindow(fftSize);
    windowedSignal = paddedSignal.map((val, i) => val * window[i]);
  }

  // Convert to complex
  const complexInput: Complex[] = windowedSignal.map((val) => ({
    real: val,
    imag: 0,
  }));

  // Compute FFT
  const fftResult = fft(complexInput);

  // Extract positive frequencies only (Nyquist)
  const halfSize = Math.floor(fftSize / 2) + 1;
  const frequencies: number[] = new Array(halfSize);
  const power: number[] = new Array(halfSize);
  const phase: number[] = new Array(halfSize);

  for (let i = 0; i < halfSize; i++) {
    frequencies[i] = (i * samplingRate) / fftSize;

    // Power = |FFT|² / N
    const magnitude = Math.sqrt(
      fftResult[i].real ** 2 + fftResult[i].imag ** 2,
    );
    power[i] = magnitude ** 2 / fftSize;

    // Phase
    phase[i] = Math.atan2(fftResult[i].imag, fftResult[i].real);
  }

  return { frequencies, power, phase };
}

/**
 * Welch's Method for Power Spectral Density Estimation
 * Reduces noise by averaging multiple overlapping segments
 */
export function welchMethod(
  signal: number[],
  samplingRate: number,
  segmentLengthOrOptions:
    | number
    | {
        segmentSize?: number;
        overlap?: number;
        windowType?: "hamming" | "hanning";
      } = 256,
  overlap: number = 0.5,
  windowType: "hamming" | "hanning" = "hamming",
): WelchResult {
  // Handle both old and new API
  let segmentLength: number;
  let actualOverlap: number;
  let actualWindowType: "hamming" | "hanning";

  if (typeof segmentLengthOrOptions === "object") {
    // New API: options object
    segmentLength = segmentLengthOrOptions.segmentSize || 256;
    actualOverlap = segmentLengthOrOptions.overlap ?? 0.5;
    actualWindowType = segmentLengthOrOptions.windowType || "hamming";
  } else {
    // Old API: individual parameters
    segmentLength = segmentLengthOrOptions;
    actualOverlap = overlap;
    actualWindowType = windowType;
  }

  // Calculate segment parameters
  const fftSize = nextPowerOf2(segmentLength);
  const hopSize = Math.floor(segmentLength * (1 - actualOverlap));
  const numSegments = Math.floor((signal.length - segmentLength) / hopSize) + 1;

  if (numSegments < 1) {
    // Signal too short: return empty PSD result instead of throwing so callers can
    // handle this gracefully (tests expect tolerant behavior).
    const halfSizeEmpty = Math.floor(fftSize / 2) + 1;
    const emptyPsd = new Array(halfSizeEmpty).fill(0);
    const freqsEmpty = new Array(halfSizeEmpty).fill(0);
    const emptyBands = {
      delta: { power: 0, range: [0.5, 4] as [number, number] },
      theta: { power: 0, range: [4, 8] as [number, number] },
      alpha: { power: 0, range: [8, 13] as [number, number] },
      beta: { power: 0, range: [13, 30] as [number, number] },
      gamma: { power: 0, range: [30, 100] as [number, number] },
    };
    return {
      frequencies: freqsEmpty,
      psd: emptyPsd,
      power: emptyPsd,
      bands: emptyBands,
    };
  }

  // Initialize accumulator for averaging
  const halfSize = Math.floor(fftSize / 2) + 1;
  const psdAccumulator: number[] = new Array(halfSize).fill(0);

  // Process each segment
  for (let i = 0; i < numSegments; i++) {
    const start = i * hopSize;
    const end = start + segmentLength;
    const segment = signal.slice(start, end);

    // Compute FFT for this segment
    const fftResult = computeFFT(segment, samplingRate, actualWindowType);

    // Accumulate power
    for (let j = 0; j < halfSize; j++) {
      psdAccumulator[j] += fftResult.power[j];
    }
  }

  // Average
  const psd = psdAccumulator.map((val) => val / numSegments);

  // Generate frequency bins
  const frequencies: number[] = new Array(halfSize);
  for (let i = 0; i < halfSize; i++) {
    frequencies[i] = (i * samplingRate) / fftSize;
  }

  // Calculate frequency band powers
  const bands = calculateBandPowers(frequencies, psd);

  return { frequencies, psd, power: psd, bands };
}

/**
 * Calculate power in standard EEG frequency bands
 */
function calculateBandPowers(frequencies: number[], psd: number[]) {
  const bands = {
    delta: { power: 0, range: [0.5, 4] as [number, number] },
    theta: { power: 0, range: [4, 8] as [number, number] },
    alpha: { power: 0, range: [8, 13] as [number, number] },
    beta: { power: 0, range: [13, 30] as [number, number] },
    gamma: { power: 0, range: [30, 100] as [number, number] },
  };

  // Integrate power over each band using trapezoidal rule
  for (const band of Object.values(bands)) {
    const [lowFreq, highFreq] = band.range;

    for (let i = 0; i < frequencies.length - 1; i++) {
      const f1 = frequencies[i];
      const f2 = frequencies[i + 1];

      // Check if this bin falls within the band
      if (f1 >= lowFreq && f2 <= highFreq) {
        // Trapezoidal integration
        const df = f2 - f1;
        const avgPower = (psd[i] + psd[i + 1]) / 2;
        band.power += avgPower * df;
      }
    }
  }

  return bands;
}

/**
 * Compute spectrogram (Short-Time Fourier Transform)
 */
export function computeSpectrogram(
  signal: number[],
  samplingRate: number,
  windowSize: number = 256,
  hopSize: number = 128,
  windowType: "hamming" | "hanning" = "hamming",
): SpectrogramData {
  const fftSize = nextPowerOf2(windowSize);
  const numFrames = Math.floor((signal.length - windowSize) / hopSize) + 1;
  const halfSize = Math.floor(fftSize / 2) + 1;

  // Initialize output arrays
  const times: number[] = new Array(numFrames);
  const frequencies: number[] = new Array(halfSize);
  const magnitudes: number[][] = Array(numFrames)
    .fill(null)
    .map(() => new Array(halfSize));

  // Generate frequency bins
  for (let i = 0; i < halfSize; i++) {
    frequencies[i] = (i * samplingRate) / fftSize;
  }

  // Process each frame
  for (let frame = 0; frame < numFrames; frame++) {
    const start = frame * hopSize;
    const end = start + windowSize;
    const segment = signal.slice(start, end);

    // Time stamp for this frame (center of window)
    times[frame] = (start + windowSize / 2) / samplingRate;

    // Compute FFT
    const fftResult = computeFFT(segment, samplingRate, windowType);

    // Store magnitudes (convert to dB)
    for (let i = 0; i < halfSize; i++) {
      // Convert power to dB: 10 * log10(power)
      // Add small epsilon to avoid log(0)
      magnitudes[frame][i] = 10 * Math.log10(fftResult.power[i] + 1e-10);
    }
  }

  return { times, frequencies, magnitudes };
}

/**
 * Apply notch filter to remove line noise (50/60 Hz)
 */
export function notchFilter(
  signal: number[],
  samplingRate: number,
  notchFreq: number = 60,
  bandwidth: number = 2,
): number[] {
  // Simple IIR notch filter implementation
  const omega = (2 * Math.PI * notchFreq) / samplingRate;
  const bw = (2 * Math.PI * bandwidth) / samplingRate;

  const r = 1 - bw / 2;
  const K = (1 - 2 * r * Math.cos(omega) + r * r) / (2 - 2 * Math.cos(omega));

  // Filter coefficients
  const b = [K, -2 * K * Math.cos(omega), K];
  const a = [1, -2 * r * Math.cos(omega), r * r];

  // Apply filter using direct form II
  const filtered = new Array(signal.length);
  let w1 = 0,
    w2 = 0;

  for (let i = 0; i < signal.length; i++) {
    const w0 = signal[i] - a[1] * w1 - a[2] * w2;
    filtered[i] = b[0] * w0 + b[1] * w1 + b[2] * w2;
    w2 = w1;
    w1 = w0;
  }

  return filtered;
}

/**
 * Calculate total power in signal
 */
export function calculateTotalPower(
  psd: number[],
  frequencies: number[],
): number {
  let totalPower = 0;
  for (let i = 0; i < psd.length - 1; i++) {
    const df = frequencies[i + 1] - frequencies[i];
    const avgPower = (psd[i] + psd[i + 1]) / 2;
    totalPower += avgPower * df;
  }
  return totalPower;
}

/**
 * Calculate relative band powers (percentage of total)
 */
export function calculateRelativeBandPowers(
  bandsOrResult: WelchResult["bands"] | WelchResult,
): {
  delta: number;
  theta: number;
  alpha: number;
  beta: number;
  gamma: number;
} {
  // Handle both WelchResult and bands object
  const bands = "bands" in bandsOrResult ? bandsOrResult.bands : bandsOrResult;

  const totalPower =
    bands.delta.power +
    bands.theta.power +
    bands.alpha.power +
    bands.beta.power +
    bands.gamma.power;

  if (!totalPower || !isFinite(totalPower) || totalPower <= 0) {
    return { delta: 0, theta: 0, alpha: 0, beta: 0, gamma: 0 };
  }

  return {
    delta: (bands.delta.power / totalPower) * 100,
    theta: (bands.theta.power / totalPower) * 100,
    alpha: (bands.alpha.power / totalPower) * 100,
    beta: (bands.beta.power / totalPower) * 100,
    gamma: (bands.gamma.power / totalPower) * 100,
  };
}
